import type { CSSProperties, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

type PaperTone = 'card' | 'paper' | 'sheet'

const TONES: Record<PaperTone, string> = {
    card: 'bg-card',
    paper: 'bg-paper',
    sheet: 'bg-[rgb(250_249_246/0.4)]'
}

interface TornPaperProps {
    children?: ReactNode
    className?: string
    /** Classes for the frayed background layer (its colour or wash). */
    surfaceClassName?: string
    tone?: PaperTone
    style?: CSSProperties
    /** Inset of the frayed surface inside the box, for seats where a sticker overhangs. */
    inset?: number
}

/**
 * A sheet of paper with softly frayed edges. The fray lives on a background-only layer, so the
 * content above it stays sharp. Corners are barely eased, the way stacked paper is.
 */
export const TornPaper: FC<TornPaperProps> = ({ children, className, surfaceClassName, tone = 'card', style, inset = 0 }): ReactNode => {
    return (
        <div className={cn('relative isolate', className)} style={style}>
            <span
                aria-hidden='true'
                className={cn('fray pointer-events-none absolute -z-10 rounded-[2px]', TONES[tone], surfaceClassName)}
                style={{ inset }}
            />
            {children}
        </div>
    )
}

interface CardFrameProps {
    children: ReactNode
    className?: string
    windowClassName?: string
}

/** Card anatomy: a 16px card with a rim floating 4px outside it, holding a recessed 8px window. */
export const CardFrame: FC<CardFrameProps> = ({ children, className, windowClassName }): ReactNode => {
    return (
        <div className={cn('card-frame', className)}>
            <div className={cn('card-window', windowClassName)}>
                {children}
            </div>
        </div>
    )
}

interface TornCornerProps {
    className?: string
}

/** The torn-off top-left corner of a large paper panel: a lighter fibrous scrap with a ragged edge. */
export const TornCorner: FC<TornCornerProps> = ({ className }): ReactNode => {
    return (
        <svg viewBox='0 0 420 240' aria-hidden='true' className={cn('pointer-events-none absolute left-0 top-0 h-auto w-[min(34%,420px)]', className)}>
            <defs>
                <linearGradient id='torn-corner-fill' x1='0' y1='0' x2='1' y2='1'>
                    <stop offset='0' stopColor='#fffdf8' stopOpacity='0.95' />
                    <stop offset='1' stopColor='#f6efe6' stopOpacity='0.75' />
                </linearGradient>
            </defs>
            <path
                d='M0 0 H 418 L 402 8 L 391 6 L 372 17 L 360 15 L 341 28 L 326 27 L 309 39 L 292 40 L 276 52 L 258 54 L 243 66 L 224 68 L 208 81 L 190 84 L 175 96 L 158 99 L 142 112 L 124 116 L 110 128 L 93 133 L 78 146 L 62 151 L 48 165 L 33 171 L 21 186 L 9 194 L 0 210 Z'
                fill='url(#torn-corner-fill)'
            />
            <path
                d='M418 0 L 402 8 L 391 6 L 372 17 L 360 15 L 341 28 L 326 27 L 309 39 L 292 40 L 276 52 L 258 54 L 243 66 L 224 68 L 208 81 L 190 84 L 175 96 L 158 99 L 142 112 L 124 116 L 110 128 L 93 133 L 78 146 L 62 151 L 48 165 L 33 171 L 21 186 L 9 194 L 0 210'
                fill='none'
                stroke='#fffdf8'
                strokeWidth='3'
                strokeLinejoin='round'
                opacity='0.9'
            />
        </svg>
    )
}
